import { useState } from 'react';
import { Avatar, PlusIcon, Text, Textarea, TrashIcon, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import { cn } from '@/shared/ui/utils/tailwind';
import {
  formatNoteAuthor,
  formatNoteDate,
  getAuthorInitials,
  getAuthorColor,
} from '@/features/property-management/components/PropertyDetailView/note-utils';

const fieldClass = cn(
  'w-full min-h-[72px] resize-y px-[var(--space-3)] py-[var(--space-2)]',
  'text-[length:var(--text-body-sm)] [font-family:var(--font-family)]',
  'border border-[var(--border-subtle)] rounded-(--radius-md)',
  'bg-[var(--surface-primary)] text-[var(--text-primary)]',
  'placeholder:text-[var(--text-tertiary)]',
);

/** Minimal note shape the row needs — property/client notes both satisfy this. */
export interface NotesSectionNote {
  note_id: string;
  content: string;
  created_by: string;
  created_at: string;
}

export interface NotesSectionProps<TNote extends NotesSectionNote> {
  notes: TNote[];
  isLoading: boolean;
  /** Attempts to save the trimmed note text; returns whether it was saved (clears the draft) — any error notification is the caller's responsibility. */
  onSubmit: (content: string) => Promise<boolean>;
  /** Deletes the note after the confirm dialog is accepted — any error notification is the caller's responsibility. Return value is ignored. */
  onDelete: (noteId: string) => unknown;
  title: string;
  placeholder: string;
  addLabel: string;
  emptyLabel: string;
  deleteLabel: string;
  confirmDeleteMessage: string;
  /** Shown above the list while notes are loading; omitted entirely when not provided. */
  loadingLabel?: string;
  maxLength?: number;
  /**
   * `data-testid` values for the section wrapper, textarea, submit button and
   * empty state. Kept explicit (rather than derived from a single prefix) so
   * each caller preserves its own pre-extraction testids exactly.
   */
  testIds: {
    section: string;
    input: string;
    submit: string;
    empty?: string;
  };
  /** Prefix for each note row's `data-testid` (row becomes `${rowTestIdPrefix}-${noteId}`). */
  rowTestIdPrefix: string;
  /** Prefix for each note's delete-button `data-testid` (becomes `${deleteTestIdPrefix}-${noteId}`). */
  deleteTestIdPrefix: string;
}

/**
 * Renders a single note as a comment card with avatar, author name,
 * relative date, and content.
 *
 * @param props - The note, delete callback, delete-button label and testid prefix.
 * @returns The rendered note comment card.
 */
function NoteRow<TNote extends NotesSectionNote>({
  note,
  onDelete,
  deleteLabel,
  rowTestIdPrefix,
  deleteTestIdPrefix,
}: {
  note: TNote;
  onDelete: (id: string) => void;
  deleteLabel: string;
  rowTestIdPrefix: string;
  deleteTestIdPrefix: string;
}) {
  const authorName = formatNoteAuthor(note.created_by);
  const initials = getAuthorInitials(note.created_by);
  const avatarColor = getAuthorColor(note.created_by);
  const dateLabel = formatNoteDate(note.created_at);

  return (
    <div
      className={cn(
        'group flex gap-[var(--space-3)] p-[var(--space-3)]',
        'rounded-(--radius-md) bg-[var(--surface-primary)]',
        'border border-[var(--border-subtle)]',
        'transition-all duration-(--duration-fast)',
        'hover:shadow-[var(--shadow-sm)]',
      )}
      data-testid={`${rowTestIdPrefix}-${note.note_id}`}
    >
      <Avatar size={32} color={avatarColor} alt={authorName}>
        {initials}
      </Avatar>
      <div className="min-w-0 flex-1 flex flex-col gap-[var(--space-1)]">
        <div className="flex items-center gap-[var(--space-2)]">
          <Text variant="bodySm" fw="var(--font-medium)">
            {authorName}
          </Text>
          <Text variant="meta" tone="tertiary">
            {dateLabel}
          </Text>
        </div>
        <Text variant="bodySm" tone="secondary" className="whitespace-pre-wrap leading-relaxed">
          {note.content}
        </Text>
      </div>
      <UnstyledButton
        type="button"
        data-testid={`${deleteTestIdPrefix}-${note.note_id}`}
        aria-label={deleteLabel}
        onClick={() => onDelete(note.note_id)}
        className={cn(
          'shrink-0 self-start p-[var(--space-2)] rounded-(--radius-md)',
          'text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100',
          'hover:text-[var(--status-error-text)] hover:bg-[var(--surface-secondary)]',
          'transition-all duration-(--duration-fast)',
        )}
      >
        <TrashIcon width={14} height={14} />
      </UnstyledButton>
    </div>
  );
}

/**
 * Generic notes section: free-text comments with author, plus add and delete.
 * Shared by the client and property detail views — they only differ in the
 * hook backing the data, the i18n copy, and how a save/delete failure is
 * surfaced (each caller wires its own notification behavior via `onSubmit`
 * and `onDelete`).
 *
 * @param props - Notes, loading state, submit/delete handlers and display copy.
 * @returns The rendered notes section.
 */
export function NotesSection<TNote extends NotesSectionNote>({
  notes,
  isLoading,
  onSubmit,
  onDelete,
  title,
  placeholder,
  addLabel,
  emptyLabel,
  deleteLabel,
  confirmDeleteMessage,
  loadingLabel,
  maxLength,
  testIds,
  rowTestIdPrefix,
  deleteTestIdPrefix,
}: NotesSectionProps<TNote>) {
  const [draft, setDraft] = useState('');

  const handleSubmit = async () => {
    if (!draft.trim()) return;
    const saved = await onSubmit(draft.trim());
    if (saved) setDraft('');
  };

  const handleDelete = (noteId: string) => {
    if (!window.confirm(confirmDeleteMessage)) return;
    void onDelete(noteId);
  };

  return (
    <SectionCard title={title}>
      <div data-testid={testIds.section} className="flex flex-col gap-[var(--space-3)]">
        <div className="flex flex-col gap-[var(--space-2)]">
          <Textarea
            data-testid={testIds.input}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            rows={3}
            maxLength={maxLength}
            className={fieldClass}
          />
          <UnstyledButton
            type="button"
            data-testid={testIds.submit}
            onClick={() => void handleSubmit()}
            disabled={!draft.trim()}
            className={cn(
              'inline-flex items-center justify-center gap-[var(--space-2)] self-end',
              'px-[var(--space-3)] py-[var(--space-2)] rounded-(--radius-md)',
              'bg-[var(--seed-base)] text-[var(--button-primary-text)]',
              'text-[length:var(--text-body-sm)] disabled:opacity-50',
            )}
          >
            <PlusIcon width={14} height={14} />
            {addLabel}
          </UnstyledButton>
        </div>
        {isLoading && loadingLabel ? (
          <Text variant="small" tone="tertiary">
            {loadingLabel}
          </Text>
        ) : null}
        {!isLoading && notes.length === 0 ? (
          <Text data-testid={testIds.empty} variant="small" tone="tertiary">
            {emptyLabel}
          </Text>
        ) : null}
        {notes.map((note) => (
          <NoteRow
            key={note.note_id}
            note={note}
            onDelete={handleDelete}
            deleteLabel={deleteLabel}
            rowTestIdPrefix={rowTestIdPrefix}
            deleteTestIdPrefix={deleteTestIdPrefix}
          />
        ))}
      </div>
    </SectionCard>
  );
}

export default NotesSection;
